import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "https://qylmox.in/api";

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("authToken");

export default function MyNotificationsList() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({ ownerUser: null, deviceCount: 0 });

  const unreadCount = useMemo(
    () => notifications.filter((item) => item?.checked !== false).length,
    [notifications]
  );

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = getAuthToken();
      if (!token) {
        setNotifications([]);
        setMeta({ ownerUser: null, deviceCount: 0 });
        setError("Login token missing hai. Dobara login karke try karo.");
        return;
      }

      const response = await axios.get(`${API_BASE}/users/my-alerts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const rows = Array.isArray(response?.data?.alerts)
        ? response.data.alerts
        : [];

      setNotifications(rows);
      setMeta({
        ownerUser: response?.data?.ownerUser || null,
        deviceCount: Number(response?.data?.deviceCount || 0),
      });
    } catch (err) {
      console.error("Failed to load assigned notifications:", err);
      setNotifications([]);
      setMeta({ ownerUser: null, deviceCount: 0 });
      setError(err?.response?.data?.error || err?.response?.data?.msg || "Failed to fetch assigned notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <NotificationsNoneIcon sx={{ color: "#2563eb" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                My Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Jo alert templates aapko assign hue hain, bas wahi yahan dikhenge.
              </Typography>
              {meta.ownerUser?.username ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
                  Controlled by {meta.ownerUser.username}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={`${notifications.length} alerts`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${meta.deviceCount} vehicles`}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${unreadCount} active`}
              color={unreadCount > 0 ? "success" : "default"}
              variant={unreadCount > 0 ? "filled" : "outlined"}
            />
            <IconButton onClick={fetchNotifications}>
              <RefreshIcon />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: 2.5 }}>
          {loading ? (
            <Stack direction="row" spacing={1.25} alignItems="center">
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading assigned alerts...
              </Typography>
            </Stack>
          ) : error ? (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          ) : notifications.length === 0 ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              Koi assigned alert template nahi mila.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              {notifications.map((item) => (
                <Paper
                  key={item?.key || item?.alarmType || item?.label}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    px: 2,
                    py: 1.75,
                    backgroundColor: item?.checked === false ? "#fff" : "#f8fbff",
                    borderColor: item?.checked === false ? "#e5e7eb" : "#bfdbfe",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {item?.label || item?.alarmType || "Notification"}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={item?.alarmType || item?.key || "notification"}
                          variant="outlined"
                        />
                        {item?.checked !== false ? (
                          <Chip size="small" label="Assigned" color="success" />
                        ) : (
                          <Chip size="small" label="Off" variant="outlined" />
                        )}
                      </Stack>
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                      {`Channel: ${(Array.isArray(item?.channels) && item.channels.length ? item.channels.join(", ") : "web")} • Sound: ${item?.sound || "default"} • Priority: ${item?.priority || "medium"}`}
                    </Typography>

                    {item?.deviceCount ? (
                      <Typography variant="caption" color="text.secondary">
                        Available on {item.deviceCount} assigned vehicle{item.deviceCount === 1 ? "" : "s"}
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
